import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import ConnectEmailModal from "@/components/ConnectEmailModal";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import {
  LayoutDashboard,
  Plane,
  Building2,
  FileText,
  Users,
  Mail,
  Inbox,
  ListChecks,
  Brain,
  BarChart3,
  LogOut,
  Link2,
  Check,
  KeyRound,
} from "lucide-react";

const RESERVAS_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/itinerarios", label: "Itinerarios", icon: Plane, testid: "nav-itinerarios" },
  { to: "/proveedores", label: "Proveedores", icon: Building2, testid: "nav-proveedores" },
  { to: "/plantillas", label: "Plantillas", icon: FileText, testid: "nav-plantillas" },
  { to: "/correos", label: "Correos enviados", icon: Mail, testid: "nav-correos" },
];

const RECON_NAV = [
  { to: "/reconfirmaciones", label: "Reconfirmaciones", icon: ListChecks, testid: "nav-reconfirmaciones" },
];

export default function Layout() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [showConnect, setShowConnect] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [pendingReplies, setPendingReplies] = useState(0);
  const etapa2 = !!user?.features?.etapa2;

  useEffect(() => {
    if (!user || user.role === "reconfirmador" || !user.features?.etapa2) return;
    api.get("/inbox/replies/count")
      .then(({ data }) => setPendingReplies(data.count || 0))
      .catch(() => {});
  }, [user]);

  const onLogout = () => {
    logout();
    navigate("/login");
  };

  const onDisconnectEmail = async () => {
    if (!confirm("¿Desconectar tu cuenta de correo? Los próximos envíos volverán a ser simulados.")) return;
    await api.post("/email-account/disconnect");
    await refresh();
    toast.success("Cuenta de correo desconectada");
  };

  const initials = (user?.name || user?.email || "?")
    .split(/[\s.@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  return (
    <div className="min-h-screen flex bg-[var(--vv-bg)]">
      <aside className="w-64 shrink-0 border-r border-[var(--vv-border)] bg-white flex flex-col">
        <div className="px-5 py-5 border-b border-[var(--vv-border)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-black flex items-center justify-center overflow-hidden p-1">
              <img src="/logo-vdv.png" alt="Viaja de Verdad" className="max-h-full max-w-full object-contain"/>
            </div>
            <div>
              <div className="font-semibold tracking-tight leading-tight">Viaja de Verdad</div>
              <div className="text-[11px] text-gray-500 uppercase tracking-wider">Asistente de Reservas</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-2 py-4 space-y-0.5">
          {user?.role !== "reconfirmador" && RESERVAS_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            >
              <n.icon size={16} strokeWidth={1.8} />
              <span>{n.label}</span>
            </NavLink>
          ))}
          {user?.role !== "reconfirmador" && etapa2 && (
            <NavLink
              to="/respuestas"
              data-testid="nav-respuestas"
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            >
              <Inbox size={16} strokeWidth={1.8} />
              <span>Respuestas</span>
              {pendingReplies > 0 && (
                <span className="ml-auto text-[10px] bg-blue-600 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid="replies-pending-badge">
                  {pendingReplies}
                </span>
              )}
            </NavLink>
          )}
          {(user?.role === "admin" || user?.role === "reconfirmador") && (
            <>
              {user?.role !== "reconfirmador" && (
                <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wider text-gray-400">Operaciones</div>
              )}
              {RECON_NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                >
                  <n.icon size={16} strokeWidth={1.8} />
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </>
          )}
          {user?.role === "admin" && (
            <>
              {etapa2 && (
                <NavLink
                  to="/aprendizaje"
                  data-testid="nav-aprendizaje"
                  className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                >
                  <Brain size={16} strokeWidth={1.8} />
                  <span>Aprendizaje</span>
                </NavLink>
              )}
              <NavLink
                to="/usuarios"
                data-testid="nav-usuarios"
                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              >
                <Users size={16} strokeWidth={1.8} />
                <span>Usuarios</span>
              </NavLink>
              <NavLink
                to="/reportes"
                data-testid="nav-reportes"
                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              >
                <BarChart3 size={16} strokeWidth={1.8} />
                <span>Reporte de uso</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="border-t border-[var(--vv-border)] p-3">
          {user?.smtp_connected ? (
            <button
              onClick={onDisconnectEmail}
              className="w-full mb-2 flex items-center justify-between gap-2 px-2 py-2 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors group"
              title="Correo conectado — clic para desconectar"
              data-testid="email-status-button"
            >
              <span className="flex items-center gap-2 text-xs font-medium">
                <Check size={14}/> Correo conectado
              </span>
              <span className="text-[10px] truncate max-w-[110px] text-emerald-600">{user.smtp_email}</span>
            </button>
          ) : (
            <button
              onClick={() => setShowConnect(true)}
              className="w-full mb-2 flex items-center gap-2 px-2 py-2 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors text-xs font-medium"
              data-testid="connect-email-button"
            >
              <Link2 size={14}/> Conectar mi correo
            </button>
          )}

          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-sm">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[11px] text-gray-500 truncate">{user?.email}</div>
            </div>
            <button
              onClick={() => setShowChangePwd(true)}
              data-testid="change-password-button"
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500"
              aria-label="Cambiar mi contraseña"
              title="Cambiar mi contraseña"
            >
              <KeyRound size={16} />
            </button>
            <button
              onClick={onLogout}
              data-testid="logout-button"
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500"
              aria-label="Cerrar sesión"
              title="Cerrar sesión"
            >
              <LogOut size={16} />
            </button>
          </div>
          <div className="px-2 pb-1 text-[10px] uppercase tracking-wider text-gray-400">
            {user?.role === "admin" ? "Administrador" : user?.role === "reconfirmador" ? "Reconfirmaciones" : "Agente"}
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <div className="flex-1 p-6 lg:p-8 overflow-x-auto">
          <Outlet />
        </div>
      </main>
      {showConnect && (
        <ConnectEmailModal
          open={showConnect}
          onClose={() => setShowConnect(false)}
          currentUser={user}
          onConnected={refresh}
        />
      )}
      {showChangePwd && (
        <ChangePasswordModal open={showChangePwd} onClose={() => setShowChangePwd(false)} />
      )}
    </div>
  );
}
